import { CalendarRange } from "lucide-react"
import { parseAsString, parseAsStringLiteral, useQueryStates } from "nuqs"

import { DateRangePicker, type DateRangePickerValue } from "@/components/ui/date-range-picker"
import { FilterChip } from "@/components/ui/filter-chip"
import { tSharedReports } from "@/features/staff/reports/format"
import {
  CUSTOM_REPORT_PRESET,
  DEFAULT_REPORT_PRESET,
  REPORT_PRESETS,
  type ReportPeriodProps,
  type ReportPresetOption,
} from "@/features/staff/reports/types"

/**
 * The period control, and the sentence that says what the period actually is.
 *
 * **The label is not a month.** A period here is a SET OF CLOSED SHIFTS, so the
 * server labels it by its shift span — "Del cierre del 31 jul 02:10 al cierre
 * del 31 ago 01:45 · 31 turnos". Money that settles after the last close lands
 * in the next shift's Z and therefore in the next period, so a label reading
 * "agosto" would promise a range the figures below do not cover. That line is
 * permanent and unconditional, exactly like the till report's scope sentence,
 * and for the same reason: a manager who reconciles this against the signed
 * closes and finds it short goes looking for a thief.
 *
 * The chip labels come from the server too, through `presets`, so the screen and
 * the CSV header name the same window the same way. "Personalizado" is the one
 * label read client-side, through `ui.staff.reports.presets.custom` — it is not
 * a member of `presets`, because a chip appending `?period=custom` with no dates
 * beside it is a control that silently does the wrong thing. See
 * `Staff::ReportPeriod::CUSTOM`'s header.
 *
 * **What is "active" is read off `period` (the server's own props), never off
 * the URL.** A manipulated `from`/`to` degrades server-side to the default
 * preset, and the URL a stale bookmark carries can disagree with what actually
 * rendered; `period.preset`/`period.custom` is what the screen in front of the
 * reader actually is. nuqs is used only to WRITE the next visit.
 *
 * **Two layouts, split by CSS, not by a `useIsMobile` read.** Eight options at
 * the mobile 44px target wrap to four rows on a 375px screen, so below `md`
 * the bar collapses to a single trigger that opens `DateRangePicker` with the
 * full list — presets and custom together. At `md` and up there is room for
 * the presets as chips, with one more chip, "Personalizado", opening the same
 * picker for the custom range. `md:hidden` / `hidden md:flex` render both
 * trees and let the media query decide, the way `StatsRangeControl` already
 * does for its own two-layout split — a JS breakpoint read would flash the
 * wrong layout for one frame on first paint. The two pickers get distinct
 * `testId`s (`report-range-mobile` / `report-range`) precisely because both
 * are in the DOM at once.
 *
 * The preset lives in the URL. `.claude/rules/react.md` §3 is explicit that URL
 * state is the source of truth for filters, and it means something concrete
 * here: an owner who wants to send "mira el sábado" to their partner sends a
 * link, and a back button after tapping through four periods walks back through
 * four periods. The write goes through `NuqsInertiaAdapter`, so it is a real
 * Inertia visit and the figures come back from the server. `period`, `from` and
 * `to` are written together in one visit, never three, so a preset tap cannot
 * land between an old `from` and a new `period`.
 */

const ALL_PERIOD_VALUES = [...REPORT_PRESETS, CUSTOM_REPORT_PRESET] as const

export function ReportPeriodBar({
  period,
  presets,
}: {
  period: ReportPeriodProps
  presets: ReportPresetOption[]
}) {
  // Only the setter is used for rendering decisions -- see the header on why
  // "active" reads off `period`, the server's own props, instead.
  const [, setQuery] = useQueryStates(
    {
      period: parseAsStringLiteral(ALL_PERIOD_VALUES).withDefault(DEFAULT_REPORT_PRESET),
      from: parseAsString,
      to: parseAsString,
    },
    // nuqs drops a param equal to its default. Not here: this URL gets pasted
    // into a message ("mira el sábado") and bookmarked, and a link reading
    // `/reports` means "whatever the default is on the day you open it". An
    // explicit `?period=month` still says what it said if the server's default
    // ever moves.
    { clearOnDefault: false }
  )

  function selectPreset(preset: string) {
    // Clearing `from`/`to` matters on the way OUT of a custom range: without
    // it, tapping back to a preset would leave a stale pair of dates sitting
    // in the URL next to a preset that ignores them.
    setQuery({ period: preset as (typeof ALL_PERIOD_VALUES)[number], from: null, to: null })
  }

  function applyCustom(from: string, to: string) {
    setQuery({ period: CUSTOM_REPORT_PRESET, from, to })
  }

  const pickerValue: DateRangePickerValue = period.custom
    ? { kind: "custom", from: period.from ?? "", to: period.to ?? "" }
    : { kind: "preset", preset: period.preset }

  const activeLabel =
    presets.find((option) => option.value === period.preset)?.label ??
    tSharedReports("presets.custom")

  const pickerCopy = {
    title: tSharedReports("range.title"),
    custom: tSharedReports("presets.custom"),
    from: tSharedReports("range.from"),
    to: tSharedReports("range.to"),
    apply: tSharedReports("range.apply"),
    hint: tSharedReports("range.hint", { days: period.maxSpanDays }),
    invalid: tSharedReports("range.invalid"),
    tooLong: tSharedReports("range.too_long", { days: period.maxSpanDays }),
  }

  return (
    <div className="space-y-2" data-testid="report-period-bar">
      {/* Phone: one control, the primary control of the screen, at the full
          44px target -- an owner taps this from home before anything else.
          There is no matching UI-kit primitive for a full-width bordered
          trigger row, so this borrows `Input`'s own box directly rather than
          inventing a new visual shape for one call site. */}
      <div className="md:hidden">
        <DateRangePicker
          presets={presets}
          value={pickerValue}
          onSelectPreset={selectPreset}
          onApplyCustom={applyCustom}
          maxDate={period.latestSelectableDate}
          maxSpanDays={period.maxSpanDays}
          copy={pickerCopy}
          testId="report-range-mobile"
          trigger={
            <button
              type="button"
              className="border-input dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 flex h-11 w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 text-sm shadow-xs outline-none focus-visible:ring-[3px]"
              data-testid="report-period-trigger"
            >
              <span>{period.custom ? tSharedReports("presets.custom") : activeLabel}</span>
              <CalendarRange className="size-4 text-muted-foreground" aria-hidden="true" />
            </button>
          }
        />
      </div>

      {/* Desktop: the whole ladder is visible at once, and "Personalizado" is
          one more chip rather than a second kind of control. */}
      <div className="hidden flex-wrap gap-2 md:flex">
        {presets.map((option) => (
          <FilterChip
            key={option.value}
            active={!period.custom && period.preset === option.value}
            className="h-11 md:h-9"
            data-testid={`report-period-option-${option.value}`}
            onClick={() => selectPreset(option.value)}
          >
            {option.label}
          </FilterChip>
        ))}

        <DateRangePicker
          presets={presets}
          value={pickerValue}
          onSelectPreset={selectPreset}
          onApplyCustom={applyCustom}
          maxDate={period.latestSelectableDate}
          maxSpanDays={period.maxSpanDays}
          copy={pickerCopy}
          testId="report-range"
          trigger={
            <FilterChip
              active={period.custom}
              className="h-11 md:h-9"
              data-testid="report-period-option-custom"
            >
              {tSharedReports("presets.custom")}
            </FilterChip>
          }
        />
      </div>

      <p className="text-xs tabular-nums text-muted-foreground" data-testid="report-period-label">
        {period.label}
      </p>

      {/* The window held more closes than the server read. Named rather than
          absorbed: a total quietly taken over fewer shifts than its own label
          claims is the failure this whole epic exists to avoid. */}
      {period.truncated ? (
        <p className="text-xs text-muted-foreground" data-testid="report-period-truncated-note">
          {tSharedReports("period.notes.truncated", { count: period.maxShifts })}
        </p>
      ) : null}

      {/* The one hole the reconciliation has, named rather than absorbed. Money
          settled before the first shift's own local day start is in no Z report
          at all, so the sum-of-Z claim holds over every period except a
          restaurant's very first. */}
      {period.firstShiftEver ? (
        <p className="text-xs text-muted-foreground" data-testid="report-period-first-shift-note">
          {tSharedReports("notes.first_shift_ever")}
        </p>
      ) : null}
    </div>
  )
}
